import { useMemo, useRef, useState } from "react";

import { AVAILABLE_LANGUAGES } from "~/utils/constants";
import { MenuBottomCornersRounded, useSimpleMenuState } from "~/common/components/Menu/Menu";
import { LinkButton, LinkType } from "~/common/components/Link";
import { MENU_POS_LEFT } from "~/common/components/Menu/MenuPositions";
import { MenuItem } from "~/common/components/Menu/MenuItem";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { LanguageService } from "~/features/navigation/components/LanguageSelector/LanguageService";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { styled } from "~/common/mui";

interface Props {
  className?: string;
}

export const LanguageSelector = ({ className }: Props) => {
  const { currentLanguage, loading, languages, ...rest } = useLanguageSelector();
  const [isOpen, handleClick, handleClose, anchorEl, setAnchorEl] = useSimpleMenuState();

  const divRef = useRef<HTMLDivElement>(null);

  // Nothing to show if there is only 1 language
  if (languages.length <= 1) {
    return null;
  }

  const onClick = () => setAnchorEl(divRef.current);

  const onItemClick = (value: string) => {
    rest.onItemClick(value);
    handleClose();
  };

  const desktopItems = languages
    .filter((language) => language.value !== currentLanguage.value)
    .map(({ label, value }) => (
      <MenuItem key={value} onClick={() => onItemClick(value)}>
        {label}
        <span className={"scha__header__language__option"}> ({value})</span>
      </MenuItem>
    ));

  const topOffset = 24 + (48 - 20) / 2;

  return (
    <LanguageSelectorRoot
      className={classNames("scha__header__language", className, isOpen && "scha__header__language--isopen")}
      ref={divRef}
    >
      <LinkButton
        variant={LinkType.NAVIGATION}
        onClick={onClick}
        disabled={loading}
        data-test-id={"language-selector-dropdown"}
      >
        <span className={"scha__header__language__current"}>{currentLanguage.value}</span>
        {isOpen ? (
          <KeyboardArrowUpIcon className={"icon-margin-l scha__header__language--icon"} sx={{ fontSize: 20 }} />
        ) : (
          <KeyboardArrowDownIcon className={"icon-margin-l scha__header__language--icon"} sx={{ fontSize: 20 }} />
        )}
      </LinkButton>
      <MenuBottomCornersRounded
        open={isOpen}
        anchorEl={anchorEl}
        {...MENU_POS_LEFT}
        onClose={handleClose}
        MenuListProps={{ disablePadding: true }}
        sx={{ marginTop: `${topOffset}px` }}
      >
        {desktopItems}
      </MenuBottomCornersRounded>
    </LanguageSelectorRoot>
  );
};

const LanguageSelectorRoot = styled("div")(({ theme }) => ({
  height: "100%",
  position: "relative",
  ".scha__link--navigation": {
    position: "relative",
  },
  "&.scha__header__language--isopen .scha__header__language--icon": {
    color: theme.palette.primary.main,
  },
  "&:hover": {
    color: theme.palette.primary.main,
  },
  ".scha__header__language__option": {
    whiteSpace: "pre",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  ".scha__header__language__dropdown-mobile, .scha__header__language__dropdown-mobile-option": {
    ...theme.typography.bodyMedium,
    height: theme.spacing(6.25),
    display: "flex",
    alignItems: "center",
    paddingLeft: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [theme.breakpoints.up("tablet")]: {
    ".scha__header__language__current": {
      textTransform: "uppercase",
    },
  },
  [theme.breakpoints.down("phone")]: {
    ".scha__header__language__current": {
      textTransform: "capitalize",
    },
  },
}));

const useAvailableLanguages = () => {
  const { t } = useTranslation();
  return useMemo(
    () =>
      AVAILABLE_LANGUAGES.map((key) => ({
        label: t(`language.${key}`),
        value: key,
      })).sort((a, b) => a.label.localeCompare(b.label)),
    [],
  );
};

export const useLanguageSelector = () => {
  const [currLngCode, setCurrLngCode] = useState(LanguageService.getCurrentLanguage);
  const [disabled, setDisabled] = useState(false);

  const languages = useAvailableLanguages();

  const onItemClick = (code: string) => {
    setCurrLngCode(code);
    setDisabled(true);
    LanguageService.changeLanguage(code);
  };

  const currentLanguage = languages.find(({ value }) => value === currLngCode) ?? { label: "", value: "" };

  return {
    currentLanguage,
    loading: disabled,
    languages,
    onItemClick,
  };
};
